import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

import { API_BASE as API_URL } from '../lib/apiBase';
export default function ProtectedRoute() {
  const { user, setUser, token, setToken, isAuthenticated: authContextAuthenticated } = useAuth();
  const location = useLocation();
  
  // Three states: null = checking, true = authenticated, false = not authenticated
  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user || user || token ? true : null
  );

  useEffect(() => {
    // Skip if we already have user in context (from login)
    if (user) {
      setIsAuthenticated(true);
      return;
    }
    
    // Skip if user data passed from AuthCallback
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    // If we have a token, verify it
    if (token) {
      const checkAuth = async () => {
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include'
          });

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setIsAuthenticated(true);
            return;
          }

          // Only force logout for genuine auth failures (401 expired, 403 deactivated).
          // 5xx / 502 / 503 from the ingress and other transient errors must NOT log
          // the user out — keep the cached token and let the next request retry.
          if (response.status === 401 || response.status === 403) {
            setIsAuthenticated(false);
            setToken(null);
            localStorage.removeItem('hrms-token');
          } else {
            // Transient server error — stay authenticated optimistically.
            console.warn('Auth check transient error', response.status, '— keeping session');
            setIsAuthenticated(true);
          }
        } catch (error) {
          // Network error (offline, CORS hiccup, ingress timeout) — keep the session.
          // Forcing a logout here is what was causing random logouts in production.
          console.warn('Auth check network error — keeping session:', error?.message || error);
          setIsAuthenticated(true);
        }
      };
      checkAuth();
    } else {
      // No token, not authenticated
      setIsAuthenticated(false);
    }
  }, [location.state, token, user, setUser, setToken]);

  // Loading state
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-[#DC2626]" />
          <p className="text-slate-600 dark:text-slate-400">Authenticating...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated
  return <Outlet />;
}
